import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Users, Music, FileText, Coins, TrendingUp, Shield,
  Search, Ban, CheckCircle, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Flag, XCircle,
  Database, Ticket, Eye, UserCog, X, Clock, CreditCard, MessageSquare, Award, Crown, Loader2,
} from "lucide-react";
import AdminBackupPage from "@/pages/AdminBackupPage";
import AdminTicketSalesPage from "@/pages/AdminTicketSalesPage";
import { toast } from "sonner";

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: TrendingUp },
  { id: "users", label: "Utilisateurs", icon: Users },
  { id: "artists", label: "Artistes", icon: Music },
  { id: "posts", label: "Publications", icon: FileText },
  { id: "reports", label: "Signalements", icon: Flag },
  { id: "tickets", label: "Billetterie", icon: Ticket },
  { id: "backups", label: "Sauvegardes", icon: Database },
];

export default function AdminPage() {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reports, setReports] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [topFanLoading, setTopFanLoading] = useState(false);
  const [topFanResult, setTopFanResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch (err) { console.error(err); }
  }, [api]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users", { params: { search, page, limit: 15 } });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
  }, [api, search, page]);

  const fetchArtists = useCallback(async () => {
    try {
      const res = await api.get("/admin/artists", { params: { search, page, limit: 15 } });
      setArtists(res.data.artists);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
  }, [api, search, page]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await api.get("/admin/posts", { params: { page, limit: 15 } });
      setPosts(res.data.posts);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
  }, [api, page]);

  useEffect(() => {
    if (tab === "dashboard") fetchStats();
    if (tab === "users") fetchUsers();
    if (tab === "artists") fetchArtists();
    if (tab === "posts") fetchPosts();
  }, [tab, fetchStats, fetchUsers, fetchArtists, fetchPosts]);

  useEffect(() => { setPage(1); }, [tab, search]);

  const modifyCredits = async (userId) => {
    const val = prompt("Montant de credits a ajouter/retirer (negatif pour retirer):", "100");
    if (val === null) return;
    const credits = parseInt(val);
    if (isNaN(credits)) return toast.success("Montant invalide");
    const reason = prompt("Raison ?", "Ajustement admin");
    try {
      await api.put(`/admin/users/${userId}/credits?amount=${credits}&reason=${encodeURIComponent(reason || "Admin")}`);
      fetchUsers();
      if (userDetail?.user?.id === userId) viewUserDetail(userId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleBan = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/ban`);
      fetchUsers();
      if (userDetail?.user?.id === userId) viewUserDetail(userId);
    } catch (err) { console.error(err); }
  };

  const deleteUser = async (userId, pseudo) => {
    if (!window.confirm(`ATTENTION: Supprimer definitivement "${pseudo}" et toutes ses donnees ? Cette action est irreversible.`)) return;
    if (!window.confirm(`Derniere confirmation: Supprimer "${pseudo}" ?`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
      setSelectedUser(null);
      setUserDetail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const changeRole = async (userId, currentRole) => {
    const newRole = currentRole === "fan" ? "artist" : "fan";
    if (!window.confirm(`Changer le role de cet utilisateur en "${newRole}" ?`)) return;
    try {
      await api.put(`/admin/users/${userId}/role?new_role=${newRole}`);
      fetchUsers();
      if (userDetail?.user?.id === userId) viewUserDetail(userId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const viewUserDetail = async (userId) => {
    setSelectedUser(userId);
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setUserDetail(res.data);
    } catch {
      setUserDetail(null);
    }
    setDetailLoading(false);
  };

  const toggleVerify = async (artistId) => {
    try {
      await api.post(`/admin/artists/${artistId}/verify`);
      fetchArtists();
    } catch (err) { console.error(err); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Supprimer cette publication ?")) return;
    try {
      await api.delete(`/admin/posts/${postId}`);
      fetchPosts();
    } catch (err) { console.error(err); }
  };

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get("/reports", { params: { status: "pending" } });
      setReports(res.data.reports);
    } catch (err) { console.error(err); }
  }, [api]);

  const resolveReport = async (reportId) => {
    try {
      await api.post(`/reports/${reportId}/resolve`);
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) { console.error(err); }
  };

  const dismissReport = async (reportId) => {
    try {
      await api.post(`/reports/${reportId}/dismiss`);
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (tab === "reports") fetchReports();
  }, [tab, fetchReports]);

  const computeTopFans = async () => {
    setTopFanLoading(true);
    setTopFanResult(null);
    try {
      const res = await api.post("/topfan/compute-all");
      setTopFanResult(res.data);
    } catch (err) {
      setTopFanResult({ error: err.response?.data?.detail || "Erreur" });
    }
    setTopFanLoading(false);
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Acces reserve aux administrateurs</p>
          <button onClick={() => navigate("/")} className="mt-4 text-[#FF6B35] text-sm">Retour</button>
        </div>
      </div>
    );
  }

  const statCards = stats ? [
    { label: "Utilisateurs", value: stats.total_users, icon: Users, color: "text-blue-500" },
    { label: "Fans", value: stats.total_fans, icon: Users, color: "text-[#FF8FA3]" },
    { label: "Artistes", value: stats.total_artists, icon: Music, color: "text-[#7000FF]" },
    { label: "Publications", value: stats.total_posts, icon: FileText, color: "text-emerald-500" },
    { label: "Credits en circulation", value: stats.total_credits_circulation, icon: Coins, color: "text-[#FF6B35]" },
    { label: "Credits investis", value: stats.total_credits_invested, icon: TrendingUp, color: "text-orange-500" },
    { label: "Mises actives", value: stats.total_investments, icon: TrendingUp, color: "text-cyan-500" },
    { label: "Interactions", value: stats.total_interactions, icon: FileText, color: "text-yellow-500" },
  ] : [];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Header */}
      <header className="glass glass-border sticky top-0 z-40">
        <div className="flex items-center justify-between px-6 py-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => { logout(); navigate("/"); }} className="text-muted-foreground hover:text-[#AFA8BA]">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-heading font-bold text-xl text-gradient">ReallyFan</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20 font-medium">Admin</span>
          </div>
          <span className="text-xs text-muted-foreground">{user.email}</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                tab === id
                  ? "bg-[#FF6B35] text-white"
                  : "bg-card text-muted-foreground border border-border hover:text-card-foreground/80"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {tab === "dashboard" && (
          <div className="space-y-6" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statCards.map((s, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-4 shadow-sm">
                  <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
                  <p className="font-mono-credits font-bold text-2xl text-card-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Top Fan Weekly Rewards */}
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm" data-testid="admin-topfan-widget">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-sm text-card-foreground">Recompenses Top Fans</h3>
                    <p className="text-[10px] text-muted-foreground">Calcule les top fans de la semaine et distribue 50 credits bonus a chaque #1</p>
                  </div>
                </div>
                <button
                  onClick={computeTopFans}
                  disabled={topFanLoading}
                  className="bg-[#FF6B35] text-white font-bold px-5 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 flex items-center gap-2"
                  data-testid="compute-topfan-btn"
                >
                  {topFanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  {topFanLoading ? "Calcul..." : "Lancer le calcul"}
                </button>
              </div>
              {topFanResult && !topFanResult.error && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                  <p className="text-sm text-emerald-700">
                    <strong>{topFanResult.computed}</strong> top fan{topFanResult.computed > 1 ? "s" : ""} calcule{topFanResult.computed > 1 ? "s" : ""} sur {topFanResult.total_artists} artistes.{" "}
                    <strong>{topFanResult.rewarded}</strong> fan{topFanResult.rewarded > 1 ? "s" : ""} recompense{topFanResult.rewarded > 1 ? "s" : ""} (+{topFanResult.reward_amount} credits chacun).
                  </p>
                </div>
              )}
              {topFanResult?.error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                  <p className="text-sm text-red-700">{topFanResult.error}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div data-testid="admin-users">
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card border-border rounded-full text-card-foreground placeholder:text-muted-foreground h-10"
                data-testid="admin-search-users"
              />
            </div>

            {/* User detail modal */}
            {selectedUser && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
                <div className="bg-card rounded-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : userDetail ? (
                    <div className="p-5 space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="font-heading font-bold text-lg text-card-foreground">{userDetail.user.pseudo}</h2>
                          <p className="text-xs text-muted-foreground">{userDetail.user.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-card-foreground/80 font-medium">{userDetail.user.role}</span>
                            {userDetail.user.banned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">Banni</span>}
                          </div>
                        </div>
                        <button onClick={() => { setSelectedUser(null); setUserDetail(null); }} className="p-1.5 rounded-full hover:bg-border">
                          <X className="w-5 h-5 text-muted-foreground" />
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white border border-border rounded-xl p-3 text-center">
                          <Coins className="w-4 h-4 text-[#FF6B35] mx-auto mb-1" />
                          <p className="font-mono-credits font-bold text-lg text-card-foreground">{userDetail.user.credits_balance || 0}</p>
                          <p className="text-[10px] text-muted-foreground">Credits</p>
                        </div>
                        <div className="bg-white border border-border rounded-xl p-3 text-center">
                          <FileText className="w-4 h-4 text-[#7000FF] mx-auto mb-1" />
                          <p className="font-mono-credits font-bold text-lg text-card-foreground">{userDetail.stats.posts_count}</p>
                          <p className="text-[10px] text-muted-foreground">Publications</p>
                        </div>
                        <div className="bg-white border border-border rounded-xl p-3 text-center">
                          <MessageSquare className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
                          <p className="font-mono-credits font-bold text-lg text-card-foreground">{userDetail.stats.comments_count}</p>
                          <p className="text-[10px] text-muted-foreground">Commentaires</p>
                        </div>
                        <div className="bg-white border border-border rounded-xl p-3 text-center">
                          <Ticket className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                          <p className="font-mono-credits font-bold text-lg text-card-foreground">{userDetail.stats.tickets_count}</p>
                          <p className="text-[10px] text-muted-foreground">Billets</p>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="bg-white border border-border rounded-xl p-3 space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Inscription</span>
                          <span className="text-card-foreground font-medium">{userDetail.user.created_at ? new Date(userDetail.user.created_at).toLocaleDateString("fr-FR") : "—"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Derniere connexion</span>
                          <span className="text-card-foreground font-medium">{userDetail.user.last_login ? new Date(userDetail.user.last_login).toLocaleDateString("fr-FR") : "—"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Streak</span>
                          <span className="text-card-foreground font-medium">{userDetail.user.streak_count || 0} jours</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Niveau</span>
                          <span className="text-card-foreground font-medium">{userDetail.user.level || 1}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Encheres</span>
                          <span className="text-card-foreground font-medium">{userDetail.stats.bids_count}</span>
                        </div>
                      </div>

                      {/* Recent transactions */}
                      {userDetail.transactions.length > 0 && (
                        <div>
                          <h3 className="font-heading font-bold text-xs text-card-foreground mb-2">Dernieres transactions</h3>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {userDetail.transactions.map((t) => (
                              <div key={t.id} className="bg-white border border-border rounded-lg p-2 flex items-center justify-between text-[10px]">
                                <span className="text-muted-foreground truncate max-w-[150px]">{t.source || t.type}</span>
                                <span className={`font-mono-credits font-bold ${t.amount >= 0 ? "text-emerald-500" : "text-red-500"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                        <button
                          onClick={() => modifyCredits(userDetail.user.id)}
                          className="flex items-center gap-1.5 bg-[#FF6B35]/10 text-[#FF6B35] font-medium px-3 py-2 rounded-full text-xs hover:bg-[#FF6B35]/20 transition-colors"
                          data-testid="detail-modify-credits"
                        >
                          <Coins className="w-3.5 h-3.5" />
                          Credits
                        </button>
                        <button
                          onClick={() => changeRole(userDetail.user.id, userDetail.user.role)}
                          className="flex items-center gap-1.5 bg-[#7000FF]/10 text-[#7000FF] font-medium px-3 py-2 rounded-full text-xs hover:bg-[#7000FF]/20 transition-colors"
                          data-testid="detail-change-role"
                        >
                          <UserCog className="w-3.5 h-3.5" />
                          {userDetail.user.role === "fan" ? "Passer artiste" : "Passer fan"}
                        </button>
                        <button
                          onClick={() => toggleBan(userDetail.user.id)}
                          className={`flex items-center gap-1.5 font-medium px-3 py-2 rounded-full text-xs transition-colors ${userDetail.user.banned ? "bg-emerald-50 text-emerald-500 hover:bg-emerald-100" : "bg-amber-50 text-amber-600 hover:bg-amber-100"}`}
                          data-testid="detail-toggle-ban"
                        >
                          <Ban className="w-3.5 h-3.5" />
                          {userDetail.user.banned ? "Debannir" : "Bloquer"}
                        </button>
                        <button
                          onClick={() => deleteUser(userDetail.user.id, userDetail.user.pseudo)}
                          className="flex items-center gap-1.5 bg-red-50 text-red-500 font-medium px-3 py-2 rounded-full text-xs hover:bg-red-100 transition-colors"
                          data-testid="detail-delete-user"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">Erreur de chargement</div>
                  )}
                </div>
              </div>
            )}

            {/* User list */}
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between shadow-sm" data-testid={`user-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-bold text-sm text-card-foreground">{u.pseudo}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${u.role === "artist" ? "bg-[#7000FF]/10 text-[#7000FF]" : "bg-muted text-card-foreground/80"}`}>{u.role}</span>
                      {u.banned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">Banni</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                    <p className="text-xs text-muted-foreground font-mono-credits mt-0.5">{u.credits_balance || 0} credits | Streak: {u.streak_count || 0}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => viewUserDetail(u.id)}
                      className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-500"
                      title="Voir le detail"
                      data-testid={`view-user-${u.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => modifyCredits(u.id)}
                      className="p-2 rounded-xl bg-[#FF6B35]/10 hover:bg-[#FF6B35]/20 text-[#FF6B35]"
                      title="Modifier credits"
                      data-testid={`credits-user-${u.id}`}
                    >
                      <Coins className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleBan(u.id)}
                      className={`p-2 rounded-xl ${u.banned ? "bg-emerald-50 text-emerald-500" : "bg-red-50 text-red-500"} hover:opacity-80`}
                      title={u.banned ? "Debannir" : "Bloquer"}
                      data-testid={`ban-user-${u.id}`}
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteUser(u.id, u.pseudo)}
                      className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500"
                      title="Supprimer"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={page} total={total} limit={15} setPage={setPage} />
          </div>
        )}

        {/* Artists */}
        {tab === "artists" && (
          <div data-testid="admin-artists">
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un artiste..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card border-border rounded-full text-card-foreground placeholder:text-muted-foreground h-10"
                data-testid="admin-search-artists"
              />
            </div>
            <div className="space-y-2">
              {artists.map((a) => (
                <div key={a.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between shadow-sm">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold text-sm text-card-foreground">{a.name}</p>
                      {a.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
                    </div>
                    <p className="text-xs text-muted-foreground">{a.city} | {a.fan_count} fans | Score: {a.current_score?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{a.genres?.join(", ")}</p>
                  </div>
                  <button
                    onClick={() => toggleVerify(a.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                      a.verified
                        ? "bg-blue-50 text-blue-500 border border-blue-200"
                        : "bg-muted text-muted-foreground border border-border"
                    }`}
                    data-testid={`verify-btn-${a.id}`}
                  >
                    {a.verified ? "Verifie" : "Verifier"}
                  </button>
                </div>
              ))}
            </div>
            <Pagination page={page} total={total} limit={15} setPage={setPage} />
          </div>
        )}

        {/* Posts */}
        {tab === "posts" && (
          <div data-testid="admin-posts">
            <div className="space-y-2">
              {posts.map((p) => (
                <div key={p.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between shadow-sm">
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold text-xs text-[#FF8FA3]">{p.artist_name}</p>
                      <span className="text-[10px] text-muted-foreground">{p.type}</span>
                    </div>
                    <p className="text-sm text-card-foreground/80 truncate mt-0.5">{p.content || "(embed)"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono-credits">
                      {p.likes_count} likes | {p.comments_count} commentaires
                    </p>
                  </div>
                  <button
                    onClick={() => deletePost(p.id)}
                    className="p-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-100"
                    title="Supprimer"
                    data-testid={`delete-post-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Pagination page={page} total={total} limit={15} setPage={setPage} />
          </div>
        )}

        {/* Reports tab */}
        {tab === "reports" && (
          <div className="space-y-3">
            <h2 className="font-heading font-bold text-sm text-card-foreground">Signalements en attente ({reports.length})</h2>
            {reports.length === 0 ? (
              <div className="text-center py-8">
                <Flag className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Aucun signalement en attente</p>
              </div>
            ) : (
              reports.map((r) => (
                <div key={r.id} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3" data-testid={`report-${r.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                    <Flag className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-xs text-card-foreground">{r.reason_label}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {r.target_type} &middot; par {r.reporter_name} &middot; {new Date(r.created_at).toLocaleDateString("fr-FR")}
                    </p>
                    {r.description && <p className="text-xs text-card-foreground/80 mt-1">{r.description}</p>}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => resolveReport(r.id)} className="p-2 rounded-xl bg-emerald-50 text-emerald-500 hover:bg-emerald-100" title="Resoudre" data-testid={`resolve-report-${r.id}`}>
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button onClick={() => dismissReport(r.id)} className="p-2 rounded-xl bg-muted text-muted-foreground hover:bg-border" title="Rejeter" data-testid={`dismiss-report-${r.id}`}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tickets/Sales tab - inline component */}
        {tab === "tickets" && <AdminTicketSalesPage />}

        {/* Backups tab - inline component */}
        {tab === "backups" && <AdminBackupPage />}
      </div>
    </div>
  );
}

function Pagination({ page, total, limit, setPage }) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-3 mt-6">
      <button
        onClick={() => setPage(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="p-2 rounded-xl bg-card text-muted-foreground hover:text-card-foreground/80 border border-border disabled:opacity-30"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-xs text-muted-foreground">
        {page} / {totalPages}
      </span>
      <button
        onClick={() => setPage(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="p-2 rounded-xl bg-card text-muted-foreground hover:text-card-foreground/80 border border-border disabled:opacity-30"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
